import { useState, useEffect } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import Drawer from 'UiKit/drawer';
import { TextBold, TextRegular, TextSemiBold } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import Badge from 'UiKit/badge';
import { Iteration } from 'Component/others';
import useGetStockStatus from 'Domain/stock-promo/useGetStockStatus';
import useGetBrand from 'Domain/brand/useGetBrand';

import { toggleMultipleFilter, toggleSingleFilter } from 'View/host-stock-promo/helper/useFilterToggle';
import { useHostStockPromoState } from 'View/host-stock-promo/state';

import styles from './styles.module.css';

const StockFilterDrawer = ({ isOpen, onClose }) => {
  const { t } = useTranslation('host-stock-promo');
  const { filterState, updateFilterState } = useHostStockPromoState();
  const { data: brand } = useGetBrand();
  const { data: stockStatusOptions } = useGetStockStatus();

  const [tempFilters, setTempFilters] = useState({
    brandId: [],
    stockStatus: '',
  });

  useEffect(() => {
    if (isOpen) {
      setTempFilters({ brandId: filterState?.brandId || [], stockStatus: filterState?.stockStatus || '' });
    }
  }, [isOpen, filterState?.brandId, filterState?.stockStatus]);

  const isBrandActive = (brandValue) => {
    return tempFilters?.brandId.includes(brandValue);
  };

  const isStockStatusActive = (stockStatusValue) => {
    return tempFilters?.stockStatus === stockStatusValue;
  };

  const handleReset = () => {
    setTempFilters({ brandId: [], stockStatus: '' });
    updateFilterState({ brandId: [], stockStatus: '', page: 1 });
    onClose();
  };

  const handleApply = () => {
    updateFilterState({ brandId: tempFilters?.brandId, stockStatus: tempFilters?.stockStatus, page: 1 });
    onClose();
  };

  return (
    <Drawer height="75vh" className={styles['stok-filter-drawer-container']} isOpen={isOpen} onClose={onClose} draggable={true}>
      {/* Header */}
      <Flex className={styles['stok-filter-drawer__head-section']}>
        <TextBold fontSize="18px" color="#1A1A1A">
          Filter
        </TextBold>
      </Flex>

      {/* Content */}
      <Box className={styles['stok-filter-drawer__content-section']}>
        {/* Filters */}
        <Box className={styles['stok-filter-drawer__filter-section-container']}>
          <Flex gap="8px" direction="column">
            <TextRegular fontSize="16px" color="#1A1A1A">
              {t('filter.brand')}
            </TextRegular>
            <Box className={styles['stok-filter-drawer__filter-section']}>
              <Iteration
                items={brand}
                render={(item) => (
                  <Badge
                    key={item?.id}
                    variant="outlined"
                    className={`${styles['filter-host-stock-promo__select-badge']} ${
                      isBrandActive(item?.value) ? styles['filter-host-stock-promo__select-badge--active'] : ''
                    }`}
                    onClick={() => toggleMultipleFilter(item?.value, setTempFilters, 'brandId')}
                  >
                    {item?.value}
                  </Badge>
                )}
              />
            </Box>
          </Flex>
          <Flex gap="8px" direction="column">
            <TextRegular fontSize="16px" color="#1A1A1A">
              {t('filter.stock_status')}
            </TextRegular>
            <Box className={styles['stok-filter-drawer__filter-section']}>
              <Iteration
                items={stockStatusOptions}
                render={(item) => (
                  <Badge
                    key={item?.label}
                    variant="outlined"
                    className={`${styles['filter-host-stock-promo__select-badge']} ${
                      isStockStatusActive(item?.value) ? styles['filter-host-stock-promo__select-badge--active'] : ''
                    }`}
                    onClick={() => toggleSingleFilter(item?.value, setTempFilters, 'stockStatus')}
                  >
                    {item?.label}
                  </Badge>
                )}
              />
            </Box>
          </Flex>
        </Box>

        {/* Footer Buttons */}
        <Flex className={styles['stok-filter-drawer__footer-section']}>
          <ButtonOutline onClick={handleReset}>
            <TextSemiBold color="var(--color-info70)">{t('common:reset')}</TextSemiBold>
          </ButtonOutline>
          <ButtonSolid onClick={handleApply}>
            <TextSemiBold color="var(--color-semantic-white)">{t('common:apply')}</TextSemiBold>
          </ButtonSolid>
        </Flex>
      </Box>
    </Drawer>
  );
};

export default StockFilterDrawer;
